/**
 * `usePostalLookup` — React hook driving the Zippopotam postal-code
 * → city / state lookup.
 *
 * Behaviour:
 *   - Triggers whenever (country, postal) changes, after a debounce
 *     so we don't fire a request on every keystroke.
 *   - Cancels any in-flight request when the user types again — the
 *     last-typed value wins, stale responses are dropped.
 *   - Returns `{ result, loading, error }`. `result` is the lookup
 *     hit (city + state); `null` means no hit yet or 404.
 *   - Never throws on the React side: 404 surfaces as `result: null`
 *     and network errors surface in `error`.
 *
 * Autofill is the caller's responsibility — the hook only fetches.
 * Profile / TenantFormDialog decide *when* to write city/state into
 * the form (only-if-empty, with-confirm, etc.).
 */
import { useEffect, useRef, useState } from 'react';

import { lookupPostalCode, type PostalLookupResult } from './zippopotam';

interface LookupState {
  result: PostalLookupResult | null;
  loading: boolean;
  error: string | null;
}

const INITIAL: LookupState = { result: null, loading: false, error: null };

export function usePostalLookup(
  countryCode: string | null,
  postalCode: string,
  debounceMs = 400,
): LookupState {
  const [state, setState] = useState<LookupState>(INITIAL);

  // Track the latest request so a slow earlier one can't overwrite a
  // newer result. The AbortController would catch most cases, but
  // belt-and-braces handles the slow-network race.
  const seq = useRef(0);

  useEffect(() => {
    const code = postalCode.trim();
    // Empty / missing inputs reset the state and skip the fetch.
    if (!countryCode || !code) {
      setState(INITIAL);
      return;
    }

    const mySeq = ++seq.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setState((s) => ({ ...s, loading: true, error: null }));
      lookupPostalCode(countryCode, code, controller.signal)
        .then((result) => {
          if (mySeq !== seq.current) return; // superseded
          setState({ result, loading: false, error: null });
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          if (mySeq !== seq.current) return;
          setState({
            result: null,
            loading: false,
            error: (err as Error)?.message ?? 'lookup failed',
          });
        });
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [countryCode, postalCode, debounceMs]);

  return state;
}
